import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import './Auth.css';

function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pluginToken, setPluginToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const { login, register, googleLogin, handleGoogleCallback, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle Google OAuth callback and error messages
  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const state = searchParams.get('state');
    const isPluginAuth = state === 'plugin:obsidian';

    if (error) {
      setError(decodeURIComponent(error));
      // Clear URL parameters to prevent reload loop
      navigate('/auth', { replace: true });
    } else if (code) {
      // Check if we've already processed this code
      const processedCode = sessionStorage.getItem('processedOAuthCode');
      if (processedCode === code) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEBUG] OAuth code already processed, skipping');
        }
        return;
      }

      // Mark this code as being processed
      sessionStorage.setItem('processedOAuthCode', code);

      handleGoogleCallback(code, state)
        .then((data) => {
          // If this is plugin auth, show the token instead of redirecting
          if (isPluginAuth) {
            setPluginToken(data.token);
            return;
          }

          // Redirect info comes from the server callback response (decoded from OAuth state)
          const redirect = data?.redirect || searchParams.get('redirect');
          const action = data?.action || searchParams.get('action');

          console.log('[Auth] After Google callback:', {
            isNewUser: data?.isNewUser,
            redirect,
            action,
            allParams: Object.fromEntries(searchParams.entries())
          });

          // Store new signup flag for privacy notice and cookie consent
          if (data?.isNewUser) {
            sessionStorage.setItem('isNewSignup', 'true');
          }

          // Redirect based on whether user is new or existing
          if (data?.isNewUser) {
            navigate('/payment-setup');
          } else if (redirect) {
            // Redirect back to the page they came from, preserving action parameter
            const redirectUrl = decodeURIComponent(redirect);
            const finalUrl = action ? `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}action=${action}` : redirectUrl;
            console.log('[Auth] Redirecting to:', finalUrl);
            navigate(finalUrl);
          } else {
            console.log('[Auth] No redirect found, going to /, data.redirect:', data?.redirect);
            navigate('/');
          }
        })
        .catch((error) => {
          // Remove the processed code on error so it can be retried
          sessionStorage.removeItem('processedOAuthCode');
          setError(error.message);
          // Clear URL parameters to prevent reload loop
          if (!isPluginAuth) {
            navigate('/auth', { replace: true });
          }
        });
    }
  }, [searchParams, handleGoogleCallback, navigate]);

  // Redirect if already authenticated (unless showing plugin token)
  useEffect(() => {
    if (isAuthenticated && !pluginToken) {
      const redirect = searchParams.get('redirect');
      if (redirect) {
        navigate(decodeURIComponent(redirect));
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, navigate, searchParams, pluginToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      if (isLogin) {
        await login(email, password);
        const redirect = searchParams.get('redirect');
        navigate(redirect ? decodeURIComponent(redirect) : '/');
      } else {
        await register(email, password);
        navigate('/payment-setup');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const redirect = searchParams.get('redirect');
      const action = searchParams.get('action');

      // Pass redirect and action to googleLogin so they can be encoded in OAuth state
      await googleLogin(redirect, action);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCopyToken = () => {
    if (pluginToken) {
      navigator.clipboard.writeText(pluginToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // If we have a plugin token, show the token display UI
  if (pluginToken) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="logo">Rec-Chord</Link>
            <h1>Authentication Successful</h1>
            <p>Copy the token below and paste it into Obsidian</p>
          </div>

          <div className="auth-form-container">
            <div style={{
              background: '#f5f5f5',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              fontSize: '14px'
            }}>
              {pluginToken}
            </div>

            <button
              onClick={handleCopyToken}
              className="auth-button"
              style={{ marginBottom: '10px' }}
            >
              {copied ? 'Copied!' : 'Copy Token'}
            </button>

            <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
              After copying the token, paste it into the Obsidian plugin settings
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="logo">Rec-Chord</Link>
          <h1>Welcome to Rec-Chord</h1>
          <p>
            Sign in with Google to access your transcripts and start transcribing with our powerful AI
          </p>
        </div>

        <div className="auth-form-container">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            onClick={handleGoogleSignIn}
            className="auth-button google"
            disabled={isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {isLoading ? 'Please wait...' : 'Continue with Google'}
          </button>
        </div>

        <div className="auth-features">
          <h3>What you'll get:</h3>
          <ul>
            <li>🎯 Professional-quality transcriptions</li>
            <li>📚 Complete transcript history</li>
            <li>🔄 Auto-save and version control</li>
            <li>🔗 Share transcripts with collaborators</li>
            <li>⚡ Fast, accurate AI processing</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Auth;
